import {OperationTypes} from '@jimmyn/apollo-client';
import {Alert, Button, Modal, Typography} from 'antd';
import {
  ChargeFieldsFragment,
  Maybe,
  PaymentMethods,
  RefundPaymentInput,
  RefundReason,
  useRefundPaymentMutation
} from 'api/generated';
import {chargeQuery} from 'api/queries';
import {AnchorLink} from 'components';
import {Formik} from 'formik';
import {Form, FormItem, InputNumber, Select} from 'formik-antd';
import {FormikHelpers} from 'formik/dist/types';
import {getRefundReasonLabels} from 'lib/chargeUtils';
import {amountFromInt, amountToInt} from 'lib/utils';
import moment from 'moment';
import React, {useState} from 'react';
import * as yup from 'yup';

type Props = {payment?: Maybe<ChargeFieldsFragment>};
const {Text} = Typography;

export const RefundPayment: React.FC<Props> = ({payment}) => {
  const [isOpen, setOpen] = useState(false);
  const [refundPayment] = useRefundPaymentMutation({
    updateQuery: {query: chargeQuery, variables: {id: payment?.id}},
    operationType: OperationTypes.UPDATE
  });

  if (!payment) return null;

  const amount = amountFromInt((payment.amount || 0) - (payment.refundedAmount || 0));
  const paymentMethod = payment.paymentMethod?.method;
  const isNonRefundablePayPal =
    paymentMethod === PaymentMethods.PAYPAL &&
    moment.unix(payment.createdAt).isBefore(moment().subtract(180, 'days'));

  const validationSchema = yup.object().shape({
    amount: yup.number().required().moreThan(0).max(amount)
  });

  const initialValues: RefundPaymentInput = {
    accountId: payment.accountId,
    amount: 0,
    paymentId: payment.id,
    refundReason: RefundReason.REQUESTED_BY_CUSTOMER
  };

  const currency = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: payment?.currency || 'EUR'
  });

  const refundReasonLabels = getRefundReasonLabels();
  const formattedAmount = currency.format(amount);

  const handleRefund = async (
    {amount, refundReason, paymentId}: RefundPaymentInput,
    {setSubmitting, resetForm}: FormikHelpers<typeof initialValues>
  ) => {
    await refundPayment({
      variables: {
        input: {
          accountId: payment.accountId,
          paymentId,
          amount: amountToInt(amount),
          refundReason
        }
      }
    });
    setSubmitting(false);
    setOpen(false);
    setTimeout(resetForm, 100);
  };

  return (
    <>
      <Button onClick={() => setOpen(true)}>Refund</Button>
      <Formik
        validationSchema={validationSchema}
        initialValues={initialValues}
        onSubmit={handleRefund}>
        {({submitForm, isSubmitting, values, resetForm, setFieldValue, errors}) => (
          <Modal
            title="Refund payment"
            okText={'Refund ' + currency.format(values.amount)}
            cancelText="Cancel"
            open={isOpen}
            confirmLoading={isSubmitting}
            okButtonProps={{disabled: isNonRefundablePayPal}}
            onOk={submitForm}
            onCancel={() => {
              setOpen(false);
              setTimeout(resetForm, 100);
            }}>
            {isNonRefundablePayPal ? (
              <Text type="danger">
                PayPal payments cannot be refunded after 180 days from the transaction date.
              </Text>
            ) : (
              <Form layout="vertical">
                <Alert
                  message={
                    <>
                      <p>Refunds take 5-10 days to appear on a customer's statement.</p>
                      <p style={{marginBottom: 0}}>
                        <b>Note:</b> if you are not using a custom integration, you should do the
                        refunds from your e-commerce admin panel to avoid an inconsistent state.
                      </p>
                    </>
                  }
                  type="info"
                  showIcon
                  style={{marginBottom: 20}}
                />
                <FormItem
                  name="amount"
                  label="Amount"
                  {...(errors.amount ? {} : {help: null})}
                  extra={
                    <>
                      {formattedAmount} available for refund.{' '}
                      <AnchorLink onClick={() => setFieldValue('amount', amount)}>
                        Refund all
                      </AnchorLink>
                      .
                    </>
                  }>
                  <InputNumber
                    type="number"
                    name="amount"
                    style={{width: '100%'}}
                    precision={2}
                    addonAfter={payment.currency}
                  />
                </FormItem>
                <FormItem name="refundReason" label="Reason of the refund">
                  <Select name="refundReason">
                    {Object.values(RefundReason).map((reason) => (
                      <Select.Option key={reason} value={reason}>
                        {refundReasonLabels[reason]}
                      </Select.Option>
                    ))}
                  </Select>
                </FormItem>
                <input type="submit" value="submit" hidden />
              </Form>
            )}
          </Modal>
        )}
      </Formik>
    </>
  );
};
